import { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Calendar, MapPin, ExternalLink, Loader2, Edit2, User, Hash, Clock, IndianRupee, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import EditRSVPModal from '../components/EditRSVPModal';

const MyRSVPsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRsvp, setEditingRsvp] = useState(null);

  useEffect(() => {
    fetchMyRSVPs();
  }, []);

  const fetchMyRSVPs = async () => {
    try {
      const response = await axios.get(`${API}/rsvps/my-rsvps`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Fetch event details for each RSVP
      const rsvpsWithEvents = await Promise.all(
        response.data.map(async (rsvp) => {
          try {
            const eventResponse = await axios.get(`${API}/events/${rsvp.event_id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            return {
              ...rsvp,
              event: eventResponse.data
            };
          } catch (error) {
            console.error(`Failed to fetch event ${rsvp.event_id}:`, error);
            return rsvp;
          }
        })
      );
      
      setRsvps(rsvpsWithEvents);
    } catch (error) {
      toast.error('Failed to load RSVPs');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSuccess = () => {
    setEditingRsvp(null);
    fetchMyRSVPs();
  };

  const handleRetryPayment = async (rsvpId) => {
    try {
      // Mark payment as failed and allow re-submission
      await axios.put(
        `${API}/rsvps/${rsvpId}/payment-status`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params: { status: 'pending' }
        }
      );
      
      toast.info('Payment reset to pending. You can now resubmit your RSVP.');
      // Navigate to event to retry
      const rsvp = rsvps.find(r => r.id === rsvpId);
      if (rsvp && rsvp.event_id) {
        navigate(`/event/${rsvp.event_id}`);
      }
    } catch (error) {
      toast.error('Failed to reset payment status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-contribo-navy" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-contribo-navy from-opacity-5 via-contribo-gold via-opacity-5 to-blue-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4"
            data-testid="back-home-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
          <h1 className="text-4xl font-bold text-gray-900">My RSVPs</h1>
          <p className="text-white mt-2">View all your event contributions</p>
        </div>

        {rsvps.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No RSVPs yet</h3>
            <p className="text-gray-600 mb-6">Join an event to make your first contribution</p>
            <Button
              className="bg-gradient-to-r from-contribo-navy to-contribo-gold text-white"
              onClick={() => navigate('/')}
            >
              Explore Events
            </Button>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="rsvps-list">
            {rsvps.map((rsvp) => (
              <Card key={rsvp.id} className="glass-card event-card border-2 border-blue-200" data-testid={`rsvp-card-${rsvp.id}`}>
                <CardContent className="pt-4 md:pt-6">
                  {/* Event Title & RSVP Status Header */}
                  <div className="mb-4 pb-4 border-b-2 border-gray-200">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 flex-1">
                        {rsvp.event?.title || 'Event'}
                      </h3>
                      <span
                        className={`text-xs px-2 md:px-3 py-1 rounded-full font-semibold flex-shrink-0 ${
                          rsvp.rsvp_status === 'yes'
                            ? 'bg-green-100 text-green-700'
                            : rsvp.rsvp_status === 'no'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {rsvp.rsvp_status === 'yes' ? '✓ Coming' : rsvp.rsvp_status === 'no' ? '✗ Not Coming' : '? Maybe'}
                      </span>
                    </div>
                  </div>

                  {/* Event Information Grid */}
                  {rsvp.event && (
                    <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                      {/* Event Creator */}
                      <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                        <p className="text-xs font-bold text-blue-900 mb-1 uppercase">Invited By</p>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-blue-600 flex-shrink-0" />
                          <p className="text-sm md:text-base font-semibold text-blue-900">
                            {rsvp.event.host_name || 'Event Host'}
                          </p>
                        </div>
                      </div>

                      {/* Event ID */}
                      <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                        <p className="text-xs font-bold text-purple-900 mb-1 uppercase">Event ID</p>
                        <div className="flex items-center gap-2">
                          <Hash className="h-4 w-4 text-purple-600 flex-shrink-0" />
                          <p className="text-sm md:text-base font-mono font-semibold text-purple-900 truncate">
                            {rsvp.event.event_code || 'N/A'}
                          </p>
                        </div>
                      </div>

                      {/* Event Date & Time */}
                      <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                        <p className="text-xs font-bold text-green-900 mb-1 uppercase">Event Date & Time</p>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <div>
                            <p className="text-sm md:text-base font-semibold text-green-900">
                              {new Date(rsvp.event.date).toLocaleDateString('en-IN', {
                                weekday: 'short',
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </p>
                            <p className="text-xs text-green-700">
                              {new Date(`${rsvp.event.date}T${rsvp.event.time}`).toLocaleTimeString('en-IN', {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Event Location */}
                      <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                        <p className="text-xs font-bold text-orange-900 mb-1 uppercase">Location</p>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-orange-600 flex-shrink-0" />
                          <p className="text-sm md:text-base font-semibold text-orange-900 truncate">
                            {rsvp.event.location || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Contribution & Collection Info */}
                  <div className="mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {/* Your Contribution */}
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border-2 border-blue-300">
                        <p className="text-xs font-bold text-blue-900 mb-2 uppercase">Your Contribution</p>
                        <div className="flex items-center gap-2">
                          <IndianRupee className="h-5 w-5 text-blue-600 flex-shrink-0" />
                          <p className="text-2xl md:text-3xl font-bold text-blue-900">
                            {rsvp.total_contribution_amount ? `₹${rsvp.total_contribution_amount.toLocaleString()}` : '₹0'}
                          </p>
                        </div>
                        {rsvp.payment_status && (
                          <span
                            className={`inline-block mt-2 text-xs px-2 py-1 rounded-full font-semibold ${
                              rsvp.payment_status === 'verified'
                                ? 'bg-green-200 text-green-800'
                                : rsvp.payment_status === 'rejected'
                                ? 'bg-red-200 text-red-800'
                                : 'bg-yellow-200 text-yellow-800'
                            }`}
                          >
                            {rsvp.payment_status === 'verified' ? '✅ Verified' : 
                             rsvp.payment_status === 'rejected' ? '❌ Rejected' : '⏳ Pending'}
                          </span>
                        )}
                      </div>

                      {/* Total Raised */}
                      {rsvp.event && (
                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 border-2 border-green-300">
                          <p className="text-xs font-bold text-green-900 mb-2 uppercase">Total Raised</p>
                          <div className="flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-green-600 flex-shrink-0" />
                            <p className="text-2xl md:text-3xl font-bold text-green-900">
                              ₹{(rsvp.event.total_contributions || 0).toLocaleString()}
                            </p>
                          </div>
                          <p className="text-xs text-green-700 mt-1">
                            Verified contributions
                          </p>
                        </div>
                      )}

                      {/* Total Goal */}
                      {rsvp.event && (
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 border-2 border-purple-300">
                          <p className="text-xs font-bold text-purple-900 mb-2 uppercase">Event Goal</p>
                          <div className="flex items-center gap-2">
                            <IndianRupee className="h-5 w-5 text-purple-600 flex-shrink-0" />
                            <p className="text-2xl md:text-3xl font-bold text-purple-900">
                              ₹{rsvp.event.contribution_goals?.reduce((sum, goal) => sum + goal.target_amount, 0).toLocaleString() || 0}
                            </p>
                          </div>
                          <p className="text-xs text-purple-700 mt-1">
                            Total goal amount
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contribution Breakdown */}
                  {rsvp.contributions && rsvp.contributions.length > 0 && (
                    <div className="mb-4 bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <p className="text-xs font-bold text-gray-700 mb-2 uppercase">Your Contributions Breakdown</p>
                      <div className="space-y-2">
                        {rsvp.contributions.map((contrib, idx) => (
                          <div key={idx} className="flex justify-between items-center p-2 bg-white rounded border border-gray-200">
                            <p className="text-sm font-semibold text-gray-900">
                              {contrib.goal_title || contrib.custom_goal_title}
                            </p>
                            <p className="text-base font-bold text-green-600">
                              ₹{contrib.amount.toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* RSVP Metadata */}
                  <div className="mb-4 text-xs text-gray-600">
                    <div className="flex items-center gap-2">
                      <Clock className="h-3 w-3" />
                      <span>
                        RSVP created on{' '}
                        {new Date(rsvp.created_at).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                      {rsvp.edit_count > 0 && (
                        <span className="ml-2 text-xs text-contribo-navy font-semibold">
                          (Edited {rsvp.edit_count} time{rsvp.edit_count !== 1 ? 's' : ''})
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Guest Count */}
                  {rsvp.total_guests > 0 && (
                    <div className="bg-blue-50 p-3 rounded-lg mb-4 border border-blue-200">
                      <p className="text-xs font-bold text-blue-900 mb-2 uppercase">Guest Count</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-gray-600">Adults (Male):</span>
                          <span className="font-semibold ml-1">{rsvp.adults_male || 0}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Adults (Female):</span>
                          <span className="font-semibold ml-1">{rsvp.adults_female || 0}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Kids (Male):</span>
                          <span className="font-semibold ml-1">{rsvp.kids_male || 0}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Kids (Female):</span>
                          <span className="font-semibold ml-1">{rsvp.kids_female || 0}</span>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-blue-200">
                        <span className="text-sm font-bold text-blue-700">
                          Total Guests: {rsvp.total_guests}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Message */}
                  {rsvp.message && (
                    <div className="bg-gray-50 p-3 rounded-lg mb-4 border border-gray-200">
                      <p className="text-xs font-bold text-gray-700 mb-1 uppercase">Your Message</p>
                      <p className="text-sm text-gray-800 italic">"{rsvp.message}"</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col md:flex-row gap-2">
                    {rsvp.edit_count < 3 ? (
                      <Button
                        variant="outline"
                        onClick={() => setEditingRsvp(rsvp)}
                        data-testid={`edit-rsvp-btn-${rsvp.id}`}
                        className="border-contribo-navy text-contribo-navy hover:bg-contribo-navy hover:bg-opacity-10 flex-1"
                      >
                        <Edit2 className="mr-2 h-4 w-4" />
                        Edit RSVP ({3 - rsvp.edit_count} left)
                      </Button>
                    ) : (
                      <div className="text-xs text-gray-500 italic px-3 py-2 border border-gray-200 rounded-md text-center flex-1">
                        No edits remaining
                      </div>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/event/${rsvp.event_id}`)}
                      data-testid={`view-event-btn-${rsvp.id}`}
                      className="flex-1"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      View Event
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit RSVP Modal */}
      {editingRsvp && (
        <EditRSVPModal
          rsvp={editingRsvp}
          onClose={() => setEditingRsvp(null)}
          onSuccess={handleEditSuccess}
        />
      )}
      <Footer />
    </div>
  );
};

export default MyRSVPsPage;
